import { useState, useEffect } from "react";
import { PortfolioItem, PortfolioImage } from "@/hooks/useProfile";

export function usePortfolioModalState(item: PortfolioItem) {
  const [images, setImages] = useState<PortfolioImage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState<Record<string, boolean>>({});

  // Prepare the images array when the modal opens or item changes
  useEffect(() => {
    // Reset state when a new item is loaded
    setCurrentIndex(0);
    setImageError({});

    try {
      // Create normalized images array from item
      if (item.images && item.images.length > 0) {
        // Ensure the images array is properly formed
        const validImages = item.images.filter(
          (img) => img && typeof img === "object" && img.url
        );

        // Check for data URLs which may indicate storage issues
        const dataUrlCount = validImages.filter((img) =>
          img.url.startsWith("data:")
        ).length;
        if (dataUrlCount > 0) {
          console.warn(
            `Portfolio modal: ${dataUrlCount} images are data URLs, which may indicate storage issues`
          );
        }

        // Sort images by order if available
        const sortedImages = [...validImages].sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        );

        setImages(sortedImages);
      } else if (item.image_url) {
        // Create a single-item array from legacy image_url

        // Check if it's a data URL
        if (item.image_url.startsWith("data:")) {
          console.warn(
            "Portfolio modal: Legacy image is a data URL, which may indicate storage issues"
          );
        }

        setImages([
          {
            id: "legacy",
            url: item.image_url,
            order: 0,
            caption: "",
            isLegacy: true,
          },
        ]);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error("Error preparing images:", error);
      setImages([]);
    }
  }, [item]);

  const handleImageError = (imageId: string, url?: string) => {
    const errorMessage = `Image failed to load: ${imageId}${
      url ? ` (${url})` : ""
    }`;
    console.error(errorMessage);

    setImageError((prev) => ({
      ...prev,
      [imageId]: true,
    }));
  };

  // Get the current image
  const currentImage = images[currentIndex];

  return {
    images,
    currentIndex,
    setCurrentIndex,
    imageError,
    handleImageError,
    currentImage,
  };
}
